import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Loader2, Lock, Download, FileWarning, ChevronLeft, ChevronRight, Bell, ThumbsDown, Check, UserPlus } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { API } from "@/lib/api";
import { TenXBrandFooter } from "@/components/TenXBrandFooter";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PASS_REASONS = ["Too early for us", "Outside our thesis", "Check size mismatch", "Not enough traction yet", "No capacity right now"];

function ResponseBar({ token, visitId, companyName, viewerEmail }) {
  const [mode, setMode] = useState("idle"); // idle | pass | done-interested | done-pass
  const [email, setEmail] = useState(viewerEmail || "");
  const [busy, setBusy] = useState(false);
  const respond = async (response, reason = "") => {
    if (response === "interested" && !email.includes("@")) { setMode("email"); return; }
    setBusy(true);
    try {
      await api.post(`/deck/${token}/respond`, { visit_id: visitId, response, reason, email });
      setMode(`done-${response}`);
    } catch (err) { alert(err?.response?.data?.detail || "Could not send your response"); }
    finally { setBusy(false); }
  };
  if (mode.startsWith("done")) {
    const signupHref = `/register?role=investor&email=${encodeURIComponent(email)}`;
    return (
      <div className="border-b border-emerald-200 bg-emerald-50 px-4 py-2 text-center" data-testid="deck-response-done">
        <span className="inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs font-semibold text-emerald-700">
          <span className="inline-flex items-center gap-1.5">
            <Check className="h-3.5 w-3.5" />
            {mode === "done-interested" ? `Noted - ${companyName || "the founder"} will keep you posted.` : "Thanks for the clear answer. It was shared with the founder."}
          </span>
          {mode === "done-interested" && email.includes("@") && (
            <Link to={signupHref} className="inline-flex items-center gap-1 font-bold text-foreground underline underline-offset-2 hover:text-brand-ink" data-testid="deck-investor-signup-link">
              <UserPlus className="h-3.5 w-3.5" /> Follow every founder who keeps you posted, free
            </Link>
          )}
        </span>
      </div>
    );
  }
  return (
    <div className="border-b bg-card px-4 py-2" data-testid="deck-response-bar">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-2">
        {mode === "idle" && (
          <>
            <span className="text-xs font-medium text-muted-foreground">Where do you stand? One tap, no email thread:</span>
            <Button size="sm" className="h-7 gap-1.5 rounded-full px-3 text-xs" disabled={busy} onClick={() => respond("interested")} data-testid="deck-interested-btn">
              <Bell className="h-3 w-3" /> Keep me posted
            </Button>
            <Button size="sm" variant="outline" className="h-7 gap-1.5 rounded-full px-3 text-xs" disabled={busy} onClick={() => setMode("pass")} data-testid="deck-pass-btn">
              <ThumbsDown className="h-3 w-3" /> Pass
            </Button>
          </>
        )}
        {mode === "email" && (
          <>
            <span className="text-xs font-medium text-muted-foreground">Where should updates go?</span>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@fund.com" className="h-7 w-52 text-xs" data-testid="deck-response-email" />
            <Button size="sm" className="h-7 rounded-full px-3 text-xs" disabled={busy || !email.includes("@")} onClick={() => respond("interested")} data-testid="deck-response-email-save">Keep me posted</Button>
          </>
        )}
        {mode === "pass" && (
          <>
            <span className="text-xs font-medium text-muted-foreground">Why the pass? (helps the founder)</span>
            {PASS_REASONS.map((r) => (
              <button key={r} disabled={busy} onClick={() => respond("pass", r)}
            className="rounded-full border px-2.5 py-1 text-[11px] font-medium text-foreground transition-colors hover:border-brand/50 hover:text-brand-ink"
                data-testid={`deck-pass-reason-${PASS_REASONS.indexOf(r)}`}>{r}</button>
            ))}
            <button disabled={busy} onClick={() => respond("pass", "")} className="text-[11px] font-medium text-muted-foreground underline" data-testid="deck-pass-skip">just pass</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function DeckViewer() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [passcode, setPasscode] = useState("");
  const [visitId, setVisitId] = useState(null);
  const [entering, setEntering] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfError, setPdfError] = useState(false);
  const [width, setWidth] = useState(820);
  const visitRef = useRef(null);
  const pageRef = useRef(1);
  const pagesRef = useRef(0);
  const stageRef = useRef(null);

  const isPdf = meta && (meta.content_type || "").includes("pdf");
  const isImage = meta && (meta.content_type || "").startsWith("image/") && !(meta.content_type || "").includes("svg");
  const contentUrl = visitId ? `${API}/deck/${token}/content?visit=${visitId}` : null;

  useEffect(() => {
    api.get(`/deck/${token}`).then(({ data }) => setMeta(data))
      .catch((err) => setError(err?.response?.status === 410 ? "This link is no longer available." : "This link is invalid."));
  }, [token]);

  useEffect(() => { pageRef.current = pageNumber; }, [pageNumber]);
  useEffect(() => { pagesRef.current = numPages; }, [numPages]);

  useEffect(() => {
    if (!stageRef.current) return;
    const el = stageRef.current;
    const ro = new ResizeObserver(() => setWidth(Math.min(el.clientWidth - 4, 900)));
    ro.observe(el);
    setWidth(Math.min(el.clientWidth - 4, 900));
    return () => ro.disconnect();
  }, [visitId, isPdf]);

  useEffect(() => {
    if (!visitId) return;
    visitRef.current = visitId;
    const iv = setInterval(() => {
      if (document.visibilityState === "visible" && visitRef.current) {
        const body = { visit_id: visitRef.current, seconds: 10 };
        if (isPdf && pagesRef.current) { body.page = pageRef.current; body.total_pages = pagesRef.current; }
        api.post(`/deck/${token}/heartbeat`, body).catch(() => {});
      }
    }, 10000);
    return () => clearInterval(iv);
  }, [visitId, token, isPdf]);

  const enter = async (e) => {
    e.preventDefault();
    setEntering(true);
    try {
      const { data } = await api.post(`/deck/${token}/access`, { email, passcode });
      setVisitId(data.visit_id);
    } catch (err) { setError(""); alert(err?.response?.data?.detail || "Access denied"); }
    finally { setEntering(false); }
  };

  const download = async () => {
    try {
      await api.post(`/deck/${token}/heartbeat`, { visit_id: visitId, seconds: 0 });
      window.open(`${API}/deck/${token}/content?visit=${visitId}&download=true`, "_blank");
    } catch (e) { console.error(e); }
  };

  const onDocLoad = useCallback(({ numPages: n }) => { setNumPages(n); setPageNumber(1); }, []);

  const wmLabel = email || "Confidential";

  return (
    <div className="flex min-h-screen flex-col bg-slate-100">
      <header className="border-b bg-background">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <Link to="/"><Logo /></Link>
          {meta && visitId && meta.allow_download && (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={download} data-testid="deck-download">
              <Download className="h-3.5 w-3.5" /> Download
            </Button>
          )}
        </div>
      </header>

      {meta && visitId && <ResponseBar token={token} visitId={visitId} companyName={meta.company_name} viewerEmail={email} />}

      <main className="relative flex flex-1 flex-col">
        {meta && visitId && meta.watermark && (!isPdf || pdfError) && (
          <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden" data-testid="deck-watermark">
            <div className="absolute left-1/2 top-1/2 flex h-[220%] w-[220%] -translate-x-1/2 -translate-y-1/2 flex-wrap content-center justify-center gap-x-20 gap-y-24 opacity-[0.09]"
              style={{ transform: "translate(-50%, -50%) rotate(-28deg)" }}>
              {Array.from({ length: 80 }).map((_, i) => (
                <span key={i} className="whitespace-nowrap text-sm font-bold uppercase tracking-wide text-slate-900">{wmLabel}</span>
              ))}
            </div>
          </div>
        )}
        {error ? (
          <div className="mx-auto mt-20 max-w-md rounded-xl border border-dashed bg-background p-10 text-center" data-testid="deck-error">
            <FileWarning className="mx-auto h-8 w-8 text-muted-foreground" />
            <h1 className="mt-3 h-display text-lg font-semibold text-foreground">{error}</h1>
            <p className="mt-2 text-sm text-muted-foreground">Ask the sender for a fresh link.</p>
          </div>
        ) : !meta ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : !visitId ? (
          <Card className="mx-auto mt-16 w-full max-w-sm rounded-xl border bg-background p-6" data-testid="deck-gate">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><Lock className="h-5 w-5" /></div>
            <h1 className="mt-3 h-display text-lg font-semibold text-foreground">{meta.name}</h1>
            <p className="mt-0.5 text-xs text-muted-foreground">Shared by {meta.company_name || "a founder"} via tenx</p>
            <form onSubmit={enter} className="mt-4 space-y-3">
              {meta.require_email && (
                <div><Label>Your email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1" data-testid="deck-email" /></div>
              )}
              {meta.has_passcode && (
                <div><Label>Passcode</Label><Input value={passcode} onChange={(e) => setPasscode(e.target.value)} required className="mt-1" data-testid="deck-passcode" /></div>
              )}
              <Button type="submit" disabled={entering} className="w-full gap-2" data-testid="deck-enter">
                {entering ? <Loader2 className="h-4 w-4 animate-spin" /> : null} View document
              </Button>
            </form>
          </Card>
        ) : isPdf && !pdfError ? (
          <div ref={stageRef} className="mx-auto flex w-full max-w-4xl flex-1 flex-col items-center px-4 py-6" data-testid="deck-pdf">
            <Document file={contentUrl} onLoadSuccess={onDocLoad} onLoadError={() => setPdfError(true)}
              loading={<div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>}>
              <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
                <Page pageNumber={pageNumber} width={width} renderTextLayer={false} renderAnnotationLayer={false} />
              </div>
            </Document>
            {numPages > 0 && (
              <div className="sticky bottom-4 mt-4 flex items-center gap-3 rounded-full border bg-background/95 px-4 py-2 shadow-md backdrop-blur" data-testid="deck-pager">
                <Button variant="ghost" size="icon" className="h-8 w-8" disabled={pageNumber <= 1} onClick={() => setPageNumber((p) => Math.max(1, p - 1))} data-testid="deck-prev"><ChevronLeft className="h-4 w-4" /></Button>
                <span className="text-xs font-medium text-foreground" data-testid="deck-page-indicator">Page {pageNumber} of {numPages}</span>
                <Button variant="ghost" size="icon" className="h-8 w-8" disabled={pageNumber >= numPages} onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))} data-testid="deck-next"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            )}
          </div>
        ) : isImage ? (
          <div className="mx-auto flex w-full max-w-4xl flex-1 items-start justify-center px-4 py-6">
            <img src={contentUrl} alt={meta.name} className="max-w-full rounded-lg border bg-white shadow-sm" data-testid="deck-image" />
          </div>
        ) : (
          <div className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center px-4 py-16 text-center" data-testid="deck-no-preview">
            <FileWarning className="h-10 w-10 text-muted-foreground" />
            <p className="mt-4 text-sm font-medium text-foreground">This file type can't be previewed in the browser.</p>
            <p className="mt-1 text-xs text-muted-foreground">{meta.name}</p>
            {meta.allow_download ? (
              <Button className="mt-5 gap-1.5" onClick={download} data-testid="deck-fallback-download">
                <Download className="h-4 w-4" /> Download file
              </Button>
            ) : (
              <p className="mt-4 text-xs text-muted-foreground">Downloads are disabled for this link - ask the sender for a PDF version.</p>
            )}
          </div>
        )}
      </main>

      <TenXBrandFooter context="deck-viewer" className="bg-background" notice="Viewing is tracked" />
    </div>
  );
}
